import { Component, OnDestroy, OnInit, inject, signal, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subscription, filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  dynamicEntitiesTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicEntity } from './dynamic-api.service';
import { AiAuthoringWizardComponent } from './ai-authoring/ai-authoring-wizard.component';
import { hasAiGeneratePermission } from './ai-authoring/ai-authoring.util';

interface CreateEntityForm {
  name: string;
  displayName: string;
  description: string;
  iconKey: string;
}

@Component({
  standalone: true,
  selector: 'ain-dynamic-entities',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    TagModule,
    ConfirmDialogModule,
    FormsModule,
    AiAuthoringWizardComponent
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page
      [eyebrow]="t('dynamic.designer.title')"
      [title]="t('dynamic.entities.title')"
      [description]="t('dynamic.entities.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.designer.info.title'"
        [descriptionKey]="'dynamic.designer.info.description'"
        [bulletKeys]="[
          'dynamic.designer.info.bullets.scope',
          'dynamic.designer.info.bullets.versioning',
          'dynamic.designer.info.bullets.relationships'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (!entities().length) {
        <ain-state type="empty" [title]="t('dynamic.entities.empty')" />
        @if (canCreateEntity()) {
          <div class="empty-actions">
            @if (canGenerateWithAi()) {
              <button
                pButton
                type="button"
                severity="help"
                icon="pi pi-sparkles"
                [label]="t('dynamic.aiAuthoring.generateWithAi')"
                (click)="openAiAuthoringWizard()"></button>
            }
            <button
              pButton
              type="button"
              [label]="t('dynamic.entities.create')"
              (click)="openCreateDialog()"></button>
          </div>
        }
      } @else {
        <section class="panel">
          <div class="toolbar">
            @if (canGenerateWithAi()) {
              <button
                pButton
                type="button"
                severity="help"
                icon="pi pi-sparkles"
                [label]="t('dynamic.aiAuthoring.generateWithAi')"
                (click)="openAiAuthoringWizard()"></button>
            }
            @if (canCreateEntity()) {
              <button
                pButton
                type="button"
                [label]="t('dynamic.entities.create')"
                (click)="openCreateDialog()"></button>
            }
            <button
              pButton
              type="button"
              severity="secondary"
              [label]="t('common.refresh')"
              (click)="reload()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <p-table [value]="entities()" [paginator]="true" [rows]="15" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('columns.name') }}</th>
                <th>{{ t('columns.displayName') }}</th>
                <th>{{ t('columns.table') }}</th>
                <th>{{ t('columns.version') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th style="width: 10rem"></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr class="clickable-row" (click)="openDetail(row.id)">
                <td><button type="button" class="link-btn" (click)="openDetail(row.id); $event.stopPropagation()">{{ row.name }}</button></td>
                <td>{{ row.displayName }}</td>
                <td>{{ row.tableName }}</td>
                <td>{{ row.currentVersion }}</td>
                <td>
                  <p-tag
                    [value]="row.isActive ? t('dynamic.designer.status.active') : t('dynamic.designer.status.archived')"
                    [severity]="row.isActive ? 'success' : 'secondary'" />
                </td>
                <td class="row-actions" (click)="$event.stopPropagation()">
                  @if (!row.isActive && canActivateEntity()) {
                    <button
                      pButton
                      type="button"
                      text
                      [label]="t('dynamic.designer.activate')"
                      (click)="confirmActivate(row)"></button>
                  }
                  @if (row.isActive && canArchiveEntity()) {
                    <button
                      pButton
                      type="button"
                      text
                      severity="danger"
                      [label]="t('dynamic.designer.archive')"
                      (click)="confirmArchive(row)"></button>
                  }
                  @if (canDeleteEntity()) {
                    <button
                      pButton
                      type="button"
                      text
                      severity="danger"
                      [label]="t('dynamic.entities.delete')"
                      (click)="confirmDelete(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }

      <p-dialog
        [header]="t('dynamic.entities.create')"
        [(visible)]="createDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(520px, 92vw)' }"
        (onHide)="resetCreateForm()">
        <div class="dialog-form">
          <ain-form-field [label]="t('dynamic.designer.logicalName')" [hint]="t('dynamic.entities.namePatternHint')">
            <input pInputText [(ngModel)]="createForm.name" [placeholder]="t('dynamic.designer.logicalName')" />
          </ain-form-field>
          <ain-form-field [label]="t('columns.displayName')">
            <input pInputText [(ngModel)]="createForm.displayName" [placeholder]="t('columns.displayName')" />
          </ain-form-field>
          <ain-form-field [label]="t('dynamic.entities.descriptionField')">
            <textarea pTextarea rows="3" [(ngModel)]="createForm.description"></textarea>
          </ain-form-field>
          <ain-form-field [label]="t('dynamic.designer.iconKey')">
            <input pInputText [(ngModel)]="createForm.iconKey" [placeholder]="t('dynamic.designer.iconKey')" />
          </ain-form-field>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.create')" [disabled]="creating()" (click)="submitCreate()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />

      <ain-ai-authoring-wizard
        #entityAiWizard
        mode="entity"
        [(visible)]="aiWizardVisible" />
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .clickable-row { cursor: pointer; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
    .dialog-form { display: grid; gap: 12px; }
    .empty-actions { display: flex; justify-content: center; margin-top: -8px; }
    .live-status {
      font-size: 0.85rem;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f4f6;
      color: #6b7280;
    }
    .live-status.connected { background: #dcfce7; color: #166534; }
    .live-status.connecting, .live-status.reconnecting { background: #fef9c3; color: #854d0e; }
    .live-status.disconnected { background: #fee2e2; color: #991b1b; }
  `]
})
export class DynamicEntitiesComponent implements OnInit, OnDestroy {
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;
  private readonly api = inject(DynamicApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);
  private liveEventsSubscription: Subscription | null = null;
  private subscribedTopic = '';

  readonly entities = signal<DynamicEntity[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly creating = signal(false);

  createDialogVisible = false;
  aiWizardVisible = false;
  createForm: CreateEntityForm = this.emptyCreateForm();

  readonly entityAiWizard = viewChild<AiAuthoringWizardComponent>('entityAiWizard');

  ngOnInit(): void {
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.dynamicEntitiesChanged))
      .subscribe(() => this.reload(false));

    void this.setupLiveSubscription();
    this.reload();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    void this.unsubscribeFromTopic();
  }

  reload(showLoading = true): void {
    if (showLoading) {
      this.loading.set(true);
    }
    this.error.set(null);
    this.api.listEntities().pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: items => this.entities.set(items ?? []),
      error: err => {
        this.entities.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  openCreateDialog(): void {
    this.resetCreateForm();
    this.createDialogVisible = true;
  }

  openDetail(id: string): void {
    void this.router.navigate(['/dynamic/entities', id]);
  }

  submitCreate(): void {
    const name = this.createForm.name.trim();
    const displayName = this.createForm.displayName.trim() || name;
    if (!name) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.designer.logicalName') });
      return;
    }

    this.creating.set(true);
    this.api.createEntity({
      name,
      displayName,
      description: this.createForm.description.trim() || undefined,
      iconKey: this.createForm.iconKey.trim() || undefined
    }).pipe(
      finalize(() => this.creating.set(false))
    ).subscribe({
      next: entity => {
        this.createDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        void this.router.navigate(['/dynamic/entities', entity.id]);
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  confirmActivate(row: DynamicEntity): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.entities.confirm.activate'),
      accept: () => this.activateEntity(row.id)
    });
  }

  confirmArchive(row: DynamicEntity): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.entities.confirm.archive'),
      accept: () => this.archiveEntity(row.id)
    });
  }

  confirmDelete(row: DynamicEntity): void {
    this.confirmation.confirm({
      header: this.t('dynamic.entities.delete.confirmTitle'),
      message: this.t('dynamic.entities.delete.confirmMessage'),
      accept: () => this.deleteEntity(row.id)
    });
  }

  private activateEntity(id: string): void {
    this.api.activateEntity(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.activated') });
        this.reload();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private archiveEntity(id: string): void {
    this.api.archiveEntity(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.archived') });
        this.reload();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private deleteEntity(id: string): void {
    this.api.deleteEntity(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.entities.delete.success') });
        this.reload();
      },
      error: (err: unknown) => this.messages.add({
        severity: 'error',
        summary: this.resolveDeleteError(err)
      })
    });
  }

  canCreateEntity(): boolean {
    return this.hasEntityAction('Create');
  }

  canActivateEntity(): boolean {
    return this.hasEntityAction('Activate');
  }

  canArchiveEntity(): boolean {
    return this.hasEntityAction('Archive');
  }

  canDeleteEntity(): boolean {
    return this.hasEntityAction('Delete');
  }

  canGenerateWithAi(): boolean {
    return hasAiGeneratePermission(this.contextService.context());
  }

  openAiAuthoringWizard(): void {
    this.entityAiWizard()?.open();
  }

  private hasEntityAction(action: string): boolean {
    return this.hasPermissionAction('dynamic.entities', action);
  }

  private hasPermissionAction(permissionKey: string, action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === permissionKey);
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  private emptyCreateForm(): CreateEntityForm {
    return { name: '', displayName: '', description: '', iconKey: '' };
  }

  resetCreateForm(): void {
    this.createForm = this.emptyCreateForm();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private resolveDeleteError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      const code = typeof body?.['error'] === 'string'
        ? body['error']
        : typeof body?.['Error'] === 'string'
          ? body['Error']
          : null;

      if (code === 'dynamic_entity_referenced') {
        return this.t('dynamic.entities.delete.errorReferenced');
      }

      if (code === 'dynamic_entity_not_found' || error.status === 404) {
        return this.t('dynamic.entities.delete.errorNotFound');
      }
    }

    return this.t('dynamic.entities.delete.failed');
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.contextService.context()?.tenantId;
    if (!tenantId) return;

    this.subscribedTopic = dynamicEntitiesTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }

  private async unsubscribeFromTopic(): Promise<void> {
    if (!this.subscribedTopic) return;
    await this.liveUpdateService.unsubscribe(this.subscribedTopic);
    this.subscribedTopic = '';
  }
}
